import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import { GridColDef } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import { MovementsColumns } from 'components/common/enhanced/common-headers/movements';
import {
  labelThresholdService,
  movementReasonService,
  movementService,
  typeService
} from 'services/api';
import { useEffect, useRef, useState } from 'react';
import { IOption } from 'interfaces';
import { loadingActions } from 'features';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import toast from 'features/toast';

export const CollapsedMovements = () => {
  const translationPrefix = 'pages.movements';
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const datagridRefresh = useRef(null);
  const sap_active = useAppSelector((state) => state.auth.sap_active);

  const [movementReasons, setMovementReasons] = useState<IOption[]>([]);
  const [productTypeOptions, setProductTypeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [labelOptions, setLabelOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState<number | null>(null);

  useEffect(() => {
    dispatch(loadingActions.startLoading());
    movementReasonService
      .getAllPaginated()
      .then((res) => {
        setMovementReasons(res?.results || []);
      })
      .catch(() => {
        setMovementReasons([]);
      });
    typeService
      .getAllBaseInformation()
      .then((types: IOption[]) => {
        if (types && Array.isArray(types)) {
          setProductTypeOptions(
            types.map((type) => ({
              value: type.code,
              label: type['description'] || type.label || type.code
            }))
          );
        } else {
          setProductTypeOptions([]);
        }
      })
      .catch(() => {
        setProductTypeOptions([]);
      });
    labelThresholdService
      .getAllBaseInformation()
      .then((labels: IOption[] | undefined) => {
        if (labels && Array.isArray(labels)) {
          setLabelOptions(
            labels.map((label) => ({
              value: label.code,
              label: label.code
            }))
          );
        } else {
          setLabelOptions([]);
        }
      })
      .catch(() => {
        setLabelOptions([]);
      });
    dispatch(loadingActions.stopLoading());
  }, []);

  useEffect(() => {
    if (movementToDelete) {
      setOpenConfirmationDialog(true);
    }
  }, [movementToDelete]);

  const handleCloseConfirmationDialog = () => {
    setOpenConfirmationDialog(false);
    setMovementToDelete(null);
  };

  const columns: GridColDef[] = MovementsColumns(
    (movementReasons || []).map((reason) => reason.code),
    true,
    false,
    true,
    setMovementToDelete,
    true,
    sap_active,
    true,
    productTypeOptions || [],
    labelOptions || []
  );

  const deleteMovement = () => {
    movementService.delete(movementToDelete).then((res) => {
      if (res?.status !== 204) {
        return;
      }
      toast.success(t('actions.successfully.deleted'));
      datagridRefresh.current();
    });
    setOpenConfirmationDialog(false);
    setMovementToDelete(null);
  };

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}>
      <EnhancedDataGrid
        columns={columns}
        service={movementService}
        outerParams={{
          pk: parseInt(searchParams.get('movement__id')),
          product__id: parseInt(searchParams.get('product__id')),
          warehouse_code: searchParams.get('warehouse_code'),
          type: searchParams.get('type'),
          author: searchParams.get('author'),
          product_batch: searchParams.get('product_batch'),
          supplier: searchParams.get('supplier'),
          purchase_order: searchParams.get('purchase_order'),
          timestamp: searchParams.get('timestamp'),
          discounted: searchParams.get('discounted')
        }}
        refresh={datagridRefresh}
      />
      <div>
        {/* Confirmation dialog PRE-ConfirmationButtonDelete */}
        <Dialog
          open={openConfirmationDialog}
          onClose={handleCloseConfirmationDialog}
          fullWidth
          maxWidth="md">
          <DialogTitle id="alert-dialog-title">
            {t(`${translationPrefix}.delete-confirmation`)}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <p>{t(`${translationPrefix}.delete-confirmation-extended`)}</p>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="w-full flex justify-between">
              <Button onClick={handleCloseConfirmationDialog}>{t('actions.cancel')}</Button>
              <div>
                <Button variant="contained" color="error" onClick={deleteMovement} autoFocus>
                  {t('actions.delete')}
                </Button>
              </div>
            </div>
          </DialogActions>
        </Dialog>
      </div>
    </TenantPage>
  );
};

export default CollapsedMovements;
